/**
 * Syllabus Viewer Component
 * Displays parsed syllabus data with structured information
 * Supports editing exams, assignments, and other extracted data
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useUpdateSyllabusParsedData } from '@/hooks/use-syllabus';
import { toast } from 'sonner';
import type { CourseSyllabus, SyllabusTopic, SyllabusExam, ParsedSyllabusData } from '@/types/syllabus';
import {
  BookOpen,
  Calendar,
  Clock,
  GraduationCap,
  Mail,
  User,
  FileText,
  CheckCircle2,
  ArrowLeft,
  CalendarDays,
  ClipboardList,
  Scale,
  Sparkles,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Library,
} from 'lucide-react';

interface SyllabusViewerProps {
  syllabus: CourseSyllabus;
  onBack?: () => void;
  onGenerateStudyPlan?: () => void;
  onGenerateTasks?: () => void;
  isGeneratingTasks?: boolean;
  existingTasksCount?: number;
  hasExamsOrAssignments?: boolean;
  className?: string;
}

export function SyllabusViewer({
  syllabus,
  onBack,
  onGenerateStudyPlan,
  onGenerateTasks,
  isGeneratingTasks,
  existingTasksCount = 0,
  hasExamsOrAssignments = false,
  className,
}: SyllabusViewerProps) {
  const { parsed_data } = syllabus;
  const updateParsedData = useUpdateSyllabusParsedData();

  // Exam editing state
  const [examDialogOpen, setExamDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState<SyllabusExam | null>(null);
  const [editingExamIndex, setEditingExamIndex] = useState<number | null>(null);

  // Consolidated readings from weekly schedule
  const consolidatedReadings = useMemo(() => {
    if (!parsed_data?.weekly_schedule) return [];
    const allReadings = new Set<string>();
    parsed_data.weekly_schedule.forEach((week) => {
      week.readings?.forEach((r) => allReadings.add(r));
    });
    return Array.from(allReadings);
  }, [parsed_data?.weekly_schedule]);

  // All cases from weekly schedule
  const consolidatedCases = useMemo(() => {
    if (!parsed_data?.weekly_schedule) return [];
    const allCases = new Set<string>();
    parsed_data.weekly_schedule.forEach((week) => {
      week.cases?.forEach((c) => allCases.add(c));
    });
    return Array.from(allCases);
  }, [parsed_data?.weekly_schedule]);

  // Exam CRUD handlers
  const handleAddExam = () => {
    setEditingExam({
      name: '',
      type: 'final',
      date: undefined,
      time: undefined,
      duration_minutes: undefined,
      format: undefined,
      topics_covered: [],
      weight: undefined,
    });
    setEditingExamIndex(null);
    setExamDialogOpen(true);
  };

  const handleEditExam = (exam: SyllabusExam, index: number) => {
    setEditingExam({ ...exam });
    setEditingExamIndex(index);
    setExamDialogOpen(true);
  };

  const handleDeleteExam = async (index: number) => {
    if (!parsed_data) return;

    const updatedExams = [...(parsed_data.exams || [])];
    updatedExams.splice(index, 1);

    const updatedParsedData: ParsedSyllabusData = {
      ...parsed_data,
      exams: updatedExams,
    };

    try {
      await updateParsedData.mutateAsync({
        syllabusId: syllabus.id,
        parsedData: updatedParsedData,
      });
      toast.success('Exam deleted');
    } catch {
      toast.error('Failed to delete exam');
    }
  };

  const handleSaveExam = async (exam: SyllabusExam) => {
    if (!parsed_data) return;

    const updatedExams = [...(parsed_data.exams || [])];

    if (editingExamIndex !== null) {
      // Update existing
      updatedExams[editingExamIndex] = exam;
    } else {
      // Add new
      updatedExams.push(exam);
    }

    const updatedParsedData: ParsedSyllabusData = {
      ...parsed_data,
      exams: updatedExams,
    };

    try {
      await updateParsedData.mutateAsync({
        syllabusId: syllabus.id,
        parsedData: updatedParsedData,
      });
      setExamDialogOpen(false);
      setEditingExam(null);
      setEditingExamIndex(null);
      toast.success(editingExamIndex !== null ? 'Exam updated' : 'Exam added');
    } catch {
      toast.error('Failed to save exam');
    }
  };

  if (!parsed_data) {
    return (
      <Card className={className}>
        <CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No parsed data available for this syllabus.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0" aria-label="Go back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold">
              {parsed_data.course_name || syllabus.file_name}
            </h1>
            {parsed_data.course_code && (
              <p className="text-muted-foreground">{parsed_data.course_code}</p>
            )}
          </div>
        </div>

        {onGenerateStudyPlan && (
          <Button onClick={onGenerateStudyPlan}>
            <CalendarDays className="h-4 w-4 mr-2" />
            Generate Study Plan
          </Button>
        )}
      </div>

      {/* Generate Tasks Action */}
      {onGenerateTasks && hasExamsOrAssignments && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Generate Tasks from Syllabus</p>
                  <p className="text-sm text-muted-foreground">
                    {existingTasksCount > 0
                      ? `${existingTasksCount} task${existingTasksCount === 1 ? '' : 's'} already created`
                      : 'Auto-create tasks for exams and assignments'}
                  </p>
                </div>
              </div>
              <Button
                onClick={onGenerateTasks}
                disabled={isGeneratingTasks}
                variant={existingTasksCount > 0 ? 'outline' : 'default'}
              >
                {isGeneratingTasks
                  ? 'Generating...'
                  : existingTasksCount > 0
                  ? 'Regenerate'
                  : 'Generate Tasks'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No extractable content warning */}
      {onGenerateTasks && !hasExamsOrAssignments && syllabus.status === 'completed' && (
        <Card className="border-yellow-500/20 bg-yellow-50 dark:bg-yellow-900/10">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <p className="text-sm">
                No exams or assignments were found in this syllabus. You can still view the parsed content below.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Course Info Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-5 w-5 text-primary" />
              Course Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {parsed_data.professor_name && (
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{parsed_data.professor_name}</span>
              </div>
            )}
            {parsed_data.professor_email && (
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`mailto:${parsed_data.professor_email}`}
                  className="text-sm text-primary hover:underline"
                >
                  {parsed_data.professor_email}
                </a>
              </div>
            )}
            {(parsed_data.semester || parsed_data.year) && (
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {parsed_data.semester} {parsed_data.year}
                </span>
              </div>
            )}
            {Array.isArray(parsed_data.office_hours) && parsed_data.office_hours.length > 0 && (
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Office Hours</p>
                  {parsed_data.office_hours.map((oh, i) => (
                    <p key={i} className="text-muted-foreground">
                      {oh.day}: {oh.start_time} - {oh.end_time}
                      {oh.location && ` (${oh.location})`}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Grading Breakdown Card */}
        {parsed_data.grading_breakdown &&
          Object.keys(parsed_data.grading_breakdown).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Scale className="h-5 w-5 text-primary" />
                  Grading Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(parsed_data.grading_breakdown).map(
                    ([category, weight]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{category}</span>
                        <Badge variant="secondary">{weight}%</Badge>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          )}
      </div>

      {/* Exams */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="h-5 w-5 text-primary" />
            Exams
            {Array.isArray(parsed_data.exams) && parsed_data.exams.length > 0 && (
              <Badge variant="outline">{parsed_data.exams.length}</Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={handleAddExam}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Exam
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Array.isArray(parsed_data.exams) && parsed_data.exams.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {parsed_data.exams.map((exam, index) => (
                <ExamCard
                  key={index}
                  exam={exam}
                  formatDate={formatDate}
                  onEdit={() => handleEditExam(exam, index)}
                  onDelete={() => handleDeleteExam(index)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No exams found. Click "Add Exam" to add one manually.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Exam Edit Dialog */}
      <ExamEditDialog
        open={examDialogOpen}
        onOpenChange={setExamDialogOpen}
        exam={editingExam}
        onSave={handleSaveExam}
        isLoading={updateParsedData.isPending}
      />

      {/* Topics / Schedule */}
      {Array.isArray(parsed_data.topics) && parsed_data.topics.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-5 w-5 text-primary" />
              Course Topics
              <Badge variant="outline" className="ml-auto">
                {parsed_data.topics.length} topics
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {parsed_data.topics.map((topic, index) => (
                  <TopicItem
                    key={index}
                    topic={topic}
                    index={index}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Textbooks & Required Materials */}
      {Array.isArray(parsed_data.textbooks) && parsed_data.textbooks.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-5 w-5 text-primary" />
              Required Textbooks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {parsed_data.textbooks.map((book, index) => (
                <li key={index} className="text-sm flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{book}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Consolidated Readings & Cases */}
      {(consolidatedReadings.length > 0 || consolidatedCases.length > 0) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Library className="h-5 w-5 text-primary" />
              All Readings & Cases
              <Badge variant="outline" className="ml-auto">
                {consolidatedReadings.length + consolidatedCases.length} items
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {consolidatedCases.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    Cases ({consolidatedCases.length})
                  </h4>
                  <ScrollArea className="h-[150px]">
                    <div className="flex flex-wrap gap-1.5">
                      {consolidatedCases.map((caseName, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {caseName}
                        </Badge>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {consolidatedReadings.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    Readings ({consolidatedReadings.length})
                  </h4>
                  <ScrollArea className="h-[200px]">
                    <ul className="space-y-1">
                      {consolidatedReadings.map((reading, index) => (
                        <li key={index} className="text-sm text-muted-foreground">
                          • {reading}
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confidence Score */}
      {syllabus.extraction_confidence > 0 && (
        <div className="text-center text-xs text-muted-foreground">
          Extraction confidence: {Math.round(syllabus.extraction_confidence * 100)}%
        </div>
      )}
    </div>
  );
}

function ExamCard({
  exam,
  formatDate,
  onEdit,
  onDelete,
}: {
  exam: SyllabusExam;
  formatDate: (d: string | undefined) => string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  // Only display weight if it's a reasonable percentage (> 1% and <= 100%)
  // Low values like 1% are likely parsing errors (e.g., 0.01 misinterpreted)
  const hasValidWeight =
    typeof exam.weight === 'number' && exam.weight > 1 && exam.weight <= 100;

  return (
    <div className="rounded-lg border p-4 group relative">
      {/* Edit/Delete buttons */}
      {(onEdit || onDelete) && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label="Edit exam">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete exam"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-2 pr-16">
        <h4 className="font-medium">{exam.name}</h4>
        <Badge variant="outline" className="capitalize">
          {exam.type}
        </Badge>
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" />
          <span>{formatDate(exam.date)}</span>
        </div>
        {hasValidWeight && (
          <div className="flex items-center gap-2">
            <Scale className="h-3.5 w-3.5" />
            <span>{exam.weight}% of grade</span>
          </div>
        )}
        {exam.format && (
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" />
            <span className="capitalize">{exam.format}</span>
          </div>
        )}
        {exam.duration_minutes && (
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" />
            <span>{exam.duration_minutes} minutes</span>
          </div>
        )}
      </div>
      {exam.topics_covered && exam.topics_covered.length > 0 && (
        <div className="mt-2 pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-1">Topics covered:</p>
          <div className="flex flex-wrap gap-1">
            {exam.topics_covered.slice(0, 5).map((topic, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {topic}
              </Badge>
            ))}
            {exam.topics_covered.length > 5 && (
              <Badge variant="secondary" className="text-xs">
                +{exam.topics_covered.length - 5} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Dialog for adding/editing exams */
function ExamEditDialog({
  open,
  onOpenChange,
  exam,
  onSave,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exam: SyllabusExam | null;
  onSave: (exam: SyllabusExam) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<SyllabusExam>({
    name: '',
    type: 'final',
    date: undefined,
    time: undefined,
    duration_minutes: undefined,
    format: undefined,
    topics_covered: [],
    weight: undefined,
  });

  // Update form when exam changes
  useState(() => {
    if (exam) {
      setFormData(exam);
    }
  });

  // Reset form when dialog opens with new exam
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && exam) {
      setFormData(exam);
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Exam name is required');
      return;
    }
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{exam?.name ? 'Edit Exam' : 'Add Exam'}</DialogTitle>
          <DialogDescription>
            {exam?.name
              ? 'Update the exam details below.'
              : 'Add a new exam with the details below.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Exam Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Final Exam, Midterm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) =>
                    setFormData({ ...formData, type: v as SyllabusExam['type'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="final">Final</SelectItem>
                    <SelectItem value="midterm">Midterm</SelectItem>
                    <SelectItem value="quiz">Quiz</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value || undefined })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="weight">Weight (%)</Label>
                <Input
                  id="weight"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g., 70"
                  value={formData.weight ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      weight: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="duration">Duration (min)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="0"
                  placeholder="e.g., 180"
                  value={formData.duration_minutes ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      duration_minutes: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="format">Format</Label>
              <Select
                value={formData.format || ''}
                onValueChange={(v) => setFormData({ ...formData, format: v || undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="essay">Essay</SelectItem>
                  <SelectItem value="multiple-choice">Multiple Choice</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                  <SelectItem value="open-book">Open Book</SelectItem>
                  <SelectItem value="closed-book">Closed Book</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                placeholder="e.g., 9:00 AM"
                value={formData.time || ''}
                onChange={(e) => setFormData({ ...formData, time: e.target.value || undefined })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TopicItem({
  topic,
  index,
  formatDate,
}: {
  topic: SyllabusTopic | string;
  index: number;
  formatDate: (d: string | undefined) => string;
}) {
  // Normalize topic - backend may return string or object
  const normalizedTopic: SyllabusTopic =
    typeof topic === 'string' ? { name: topic } : topic;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
      <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
        {normalizedTopic.week || index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">{normalizedTopic.name}</h4>
          {normalizedTopic.date && (
            <Badge variant="outline" className="text-xs">
              {formatDate(normalizedTopic.date)}
            </Badge>
          )}
        </div>
        {normalizedTopic.description && (
          <p className="text-xs text-muted-foreground mt-1">{normalizedTopic.description}</p>
        )}
        {normalizedTopic.readings && normalizedTopic.readings.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground">
              Readings: {normalizedTopic.readings.join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SyllabusViewer;
